import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, LogIn, FileUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PartnerOperations: React.FC = () => {
  const { toast } = useToast();
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  // Mock: Em produção, viria do contexto de autenticação
  const companyType = localStorage.getItem("partnerCompanyType") || "aviation"; // "aviation" | "hotel" | "sports"
  
  // Estado para Check-in Manual (Hotel)
  const [checkInData, setCheckInData] = useState({
    clienteNome: "",
    clienteEmail: "",
    clienteCPF: "",
    dataCheckIn: "",
    distancia: ""
  });

  // Estado para Upload de Voos (Aviação)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleCheckIn = () => {
    // Validação
    if (!checkInData.clienteNome || !checkInData.clienteEmail || !checkInData.dataCheckIn) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    // Em produção, enviaria para API
    toast({
      title: "Check-in registrado!",
      description: `Check-in de ${checkInData.clienteNome} registrado com sucesso.`,
    });

    // Limpar formulário
    setCheckInData({
      clienteNome: "",
      clienteEmail: "",
      clienteCPF: "",
      dataCheckIn: "",
      distancia: ""
    });
    setIsCheckInModalOpen(false);
  };

  const handleFileUpload = () => {
    if (!selectedFile) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo.",
        variant: "destructive"
      });
      return;
    }

    // Em produção, enviaria para API
    toast({
      title: "Arquivo enviado!",
      description: `${selectedFile.name} foi processado com sucesso.`,
    });

    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Operações</h1>
        <p className="text-gray-600 mt-1">Gerencie as operações da sua empresa</p>
      </div>

      {companyType === "hotel" ? (
        /* Interface para Hotel - Check-in Manual */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              Realizar Check-in Manual
            </CardTitle>
            <CardDescription>
              Registre manualmente o check-in de um hóspede para gerar compensação de carbono
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={isCheckInModalOpen} onOpenChange={setIsCheckInModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                  <LogIn className="mr-2 h-4 w-4" />
                  Novo Check-in
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Registrar Check-in</DialogTitle>
                  <DialogDescription>
                    Preencha os dados do hóspede para calcular a compensação
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="clienteNome">Nome do Hóspede *</Label>
                    <Input
                      id="clienteNome"
                      value={checkInData.clienteNome}
                      onChange={(e) => setCheckInData({ ...checkInData, clienteNome: e.target.value })}
                      placeholder="João Silva"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clienteEmail">Email *</Label>
                    <Input
                      id="clienteEmail"
                      type="email"
                      value={checkInData.clienteEmail}
                      onChange={(e) => setCheckInData({ ...checkInData, clienteEmail: e.target.value })}
                      placeholder="joao@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clienteCPF">CPF (opcional)</Label>
                    <Input
                      id="clienteCPF"
                      value={checkInData.clienteCPF}
                      onChange={(e) => setCheckInData({ ...checkInData, clienteCPF: e.target.value })}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dataCheckIn">Data do Check-in *</Label>
                    <Input
                      id="dataCheckIn"
                      type="date"
                      value={checkInData.dataCheckIn}
                      onChange={(e) => setCheckInData({ ...checkInData, dataCheckIn: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="distancia">Distância percorrida (km) - opcional</Label>
                    <Input
                      id="distancia"
                      type="number"
                      value={checkInData.distancia}
                      onChange={(e) => setCheckInData({ ...checkInData, distancia: e.target.value })}
                      placeholder="150"
                    />
                  </div>
                  <Button onClick={handleCheckIn} className="w-full bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                    Registrar Check-in
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        /* Interface para Aviação/Transporte - Upload de Voos */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload de Voos em Lote
            </CardTitle>
            <CardDescription>
              Faça upload de um arquivo CSV ou Excel com os dados dos voos para processamento em lote
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                  <FileUp className="mr-2 h-4 w-4" />
                  Upload de Arquivo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Upload de Voos</DialogTitle>
                  <DialogDescription>
                    Selecione um arquivo CSV ou Excel com os dados dos voos
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="fileUpload">Arquivo (CSV ou Excel)</Label>
                    <Input
                      id="fileUpload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    <p className="text-xs text-gray-500">
                      Formato esperado: Data, Origem, Destino, Distância (km), Passageiros
                    </p>
                  </div>
                  {selectedFile && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  )}
                  <Button onClick={handleFileUpload} className="w-full bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                    Processar Arquivo
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnerOperations;
